import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useBilling } from "@/contexts/BillingContext";
import { LogOut, Sparkles, BookOpen, Menu, X } from "lucide-react";
import { NAV, AUTH, BILLING } from "@/constants/testIds";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Sux1Logo } from "@/components/Sux1Logo";

const navLinkClasses = ({ isActive }) =>
  `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-2 border transition-colors ${
    isActive
      ? "bg-[#002FA7] text-white border-[#002FA7] font-semibold"
      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border hover:bg-secondary"
  }`;

export const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { billing } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();
  const guideHref = i18n.resolvedLanguage === "en" ? "/guide-en.html" : "/guide.html";
  const guideLabel = i18n.resolvedLanguage === "en" ? "Guide" : "Guide";
  // Mobile hamburger menu state. Auto-closes on route change so a tap
  // on a NavLink dismisses the drawer naturally.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  // Lock body scroll while the drawer is open so iOS doesn't bleed-through.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);
  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
      <div className="h-16 flex items-center px-6 md:px-10 justify-between gap-3">
        <Link to="/" data-testid={NAV.brand} className="flex flex-col justify-center leading-none">
          <Sux1Logo size="sm" />
          <span data-testid="nav-brand-tagline" className="mt-1 font-mono uppercase tracking-[0.16em] text-[9px] text-muted-foreground whitespace-nowrap">
            {t("nav.tagline", "Résumeur de textes")}
          </span>
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" end className={navLinkClasses} data-testid={NAV.dashboardLink} data-tip={t("nav_tip.summarize", "Résumer : la page principale. Collez, importez ou scannez un texte, puis résumez-le.")}>
              {t("nav.summarize")}
            </NavLink>
            <NavLink to="/history" className={navLinkClasses} data-testid={NAV.historyLink} data-tip={t("nav_tip.history", "Historique : retrouvez, relisez, copiez ou partagez tous vos résumés.")}>
              {t("nav.history")}
            </NavLink>
            <NavLink to="/cartes" className={navLinkClasses} data-testid="nav-cards-link" data-tip={t("nav.cards_tip", "Mes cartes : révisez vos cartes question / réponse, avec rappel demain, dans 3 jours puis dans 1 semaine.")}>
              {t("nav.cards", "Cartes")}
            </NavLink>
            <NavLink to="/settings" className={navLinkClasses} data-testid={NAV.settingsLink} data-tip={t("nav_tip.settings", "Paramètres : vos réglages par défaut (IA, longueur, format), votre mot de passe et vos crédits.")}>
              {t("nav.settings")}
            </NavLink>
            <a
              href={guideHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-guide-link"
              data-tip={t("nav_tip.guide", "Guide : le mode d'emploi complet de SUX1, pas à pas (s'ouvre dans un nouvel onglet).")}
              className="font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-2 border border-transparent text-muted-foreground hover:text-foreground hover:border-border hover:bg-secondary transition-colors flex items-center gap-1.5"
            >
              <BookOpen className="w-3 h-3" />
              {guideLabel}
            </a>
            <NavLink
              to="/enseignants"
              data-testid="nav-enseignants-link"
              className={({ isActive }) =>
                `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-2 border transition-colors flex items-center gap-1.5 ${
                  isActive
                    ? "bg-[#002FA7] text-white border-[#002FA7]"
                    : "border-transparent text-muted-foreground hover:text-[#002FA7] hover:border-[#002FA7]/40 hover:bg-secondary"
                }`
              }
              data-tip={t("nav_tip.book", "Livre : « L'IA au service des enseignants », le guide pratique pour utiliser SUX1 en classe.")}
            >
              {t("nav.book", "Livre")}
              <span className="ml-1 inline-block px-1.5 py-0.5 bg-[#002FA7] text-white text-[9px] leading-none tracking-normal">
                NEW
              </span>
            </NavLink>
            <NavLink
              to="/enseignants/cas-pratiques"
              data-testid="nav-cas-pratiques-link"
              className={({ isActive }) =>
                `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-2 border transition-colors flex items-center gap-1.5 ${
                  isActive
                    ? "bg-[#002FA7] text-white border-[#002FA7]"
                    : "border-transparent text-muted-foreground hover:text-[#002FA7] hover:border-[#002FA7]/40 hover:bg-secondary"
                }`
              }
              data-tip={t("nav_tip.cases", "50 cas : des situations de classe prêtes à l'emploi (QCM, mail aux parents, DYS…), testables en 1 clic.")}
            >
              {t("nav.cases_short", "50 cas")}
            </NavLink>
          </nav>
        )}

        <div className="flex items-center gap-2 md:gap-3">
          <ThemeToggle />
          <LanguageSwitcher />
          {user ? (
            <>
              <div className="hidden lg:flex flex-col items-end leading-tight">
                <span className="text-sm font-medium flex items-center gap-2">
                  {user.name}
                  {billing?.is_pro && (
                    <span
                      data-testid={BILLING.proBadge}
                      className="inline-flex items-center gap-1 bg-[#002FA7] text-white px-1.5 py-0.5 font-mono uppercase tracking-[0.1em] text-[10px]"
                    >
                      <Sparkles className="w-2.5 h-2.5" /> {t("nav.pro")}
                    </span>
                  )}
                </span>
                <span className="sux-marker">{user.email}</span>
              </div>
              <button
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                data-testid={AUTH.logoutButton}
                data-tip={t("nav_tip.logout", "Déconnexion : fermez votre session (vos résumés restent enregistrés).")}
                className="hidden md:flex font-mono uppercase tracking-[0.14em] text-[11px] px-3 md:px-4 py-2 border border-border text-muted-foreground hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors items-center gap-2"
              >
                <LogOut className="w-3 h-3" />
                <span className="hidden sm:inline">{t("nav.logout")}</span>
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="header-signin-link"
              className="hidden md:inline-flex font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors whitespace-nowrap items-center"
            >
              {t("nav.signin")}
            </Link>
          )}
          {/* Mobile hamburger — always visible below md, for both auth
              and anonymous visitors (anon users need access to Livre,
              50 cas, Guide too). */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            data-testid="nav-mobile-open"
            aria-label={t("nav.open_menu", "Ouvrir le menu")}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-border text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ======== Mobile drawer ========
          Rendered via a Portal to document.body so it escapes the
          `sticky z-40` header stacking context and sits on top of every
          page (tabs, modals, mobile sticky CTAs, etc.). Works for both
          authenticated and anonymous visitors. */}
      {mobileOpen && createPortal(
        <div
          className="md:hidden fixed inset-0 z-[100] bg-[#002FA7]/70 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="nav-mobile-overlay"
        >
          <div
            className="absolute top-0 right-0 h-full w-[85%] max-w-sm bg-background border-l-2 border-[#002FA7] shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="nav-mobile-drawer"
            role="dialog"
            aria-modal="true"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex flex-col leading-tight min-w-0">
                {user ? (
                  <>
                    <span className="text-sm font-semibold truncate flex items-center gap-2">
                      {user.name}
                      {billing?.is_pro && (
                        <span className="inline-flex items-center gap-1 bg-[#002FA7] text-white px-1.5 py-0.5 font-mono uppercase tracking-[0.1em] text-[9px]">
                          <Sparkles className="w-2.5 h-2.5" /> {t("nav.pro")}
                        </span>
                      )}
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {user.email}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-sm font-semibold truncate">
                      {t("nav.welcome_guest", "Bienvenue")}
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate">
                      {t("nav.guest_sub", "Mode visiteur · exemples gratuits")}
                    </span>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                data-testid="nav-mobile-close"
                aria-label={t("nav.close_menu", "Fermer le menu")}
                className="shrink-0 inline-flex items-center justify-center w-9 h-9 border border-border text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Drawer nav links */}
            <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1">
              <NavLink
                to="/"
                end
                data-testid="nav-mobile-dashboard"
                className={({ isActive }) =>
                  `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-3 border transition-colors ${
                    isActive
                      ? "bg-[#002FA7] text-white border-[#002FA7]"
                      : "border-transparent text-foreground hover:bg-[#002FA7]/10 hover:text-[#002FA7]"
                  }`
                }
              >
                {t("nav.summarize")}
              </NavLink>
              {user && (
                <>
                  <NavLink
                    to="/history"
                    data-testid="nav-mobile-history"
                    className={({ isActive }) =>
                      `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-3 border transition-colors ${
                        isActive
                          ? "bg-[#002FA7] text-white border-[#002FA7]"
                          : "border-transparent text-foreground hover:bg-[#002FA7]/10 hover:text-[#002FA7]"
                      }`
                    }
                  >
                    {t("nav.history")}
                  </NavLink>
                  <NavLink
                    to="/cartes"
                    data-testid="nav-mobile-cards"
                    className={({ isActive }) =>
                      `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-3 border transition-colors ${
                        isActive
                          ? "bg-[#002FA7] text-white border-[#002FA7]"
                          : "border-transparent text-foreground hover:bg-[#002FA7]/10 hover:text-[#002FA7]"
                      }`
                    }
                  >
                    {t("nav.cards_long", "Cartes de révision")}
                  </NavLink>
                  <NavLink
                    to="/settings"
                    data-testid="nav-mobile-settings"
                    className={({ isActive }) =>
                      `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-3 border transition-colors ${
                        isActive
                          ? "bg-[#002FA7] text-white border-[#002FA7]"
                          : "border-transparent text-foreground hover:bg-[#002FA7]/10 hover:text-[#002FA7]"
                      }`
                    }
                  >
                    {t("nav.settings")}
                  </NavLink>
                </>
              )}
              <a
                href={guideHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-guide"
                className="font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-3 border border-transparent text-foreground hover:bg-[#002FA7]/10 hover:text-[#002FA7] transition-colors flex items-center gap-2"
              >
                <BookOpen className="w-3.5 h-3.5" />
                {guideLabel}
              </a>
              <NavLink
                to="/enseignants"
                data-testid="nav-mobile-enseignants"
                className={({ isActive }) =>
                  `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-3 border transition-colors flex items-center justify-between ${
                    isActive
                      ? "bg-[#002FA7] text-white border-[#002FA7]"
                      : "border-transparent text-foreground hover:bg-[#002FA7]/10 hover:text-[#002FA7]"
                  }`
                }
              >
                <span>{t("nav.book", "Livre")}</span>
                <span className="inline-block px-1.5 py-0.5 bg-[#002FA7] text-white text-[9px] leading-none tracking-normal">
                  NEW
                </span>
              </NavLink>
              <NavLink
                to="/enseignants/cas-pratiques"
                data-testid="nav-mobile-cas-pratiques"
                className={({ isActive }) =>
                  `font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-3 border transition-colors flex items-center justify-between ${
                    isActive
                      ? "bg-[#002FA7] text-white border-[#002FA7]"
                      : "border-[#002FA7]/40 text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
                  }`
                }
              >
                <span>{t("nav.cases_long", "50 cas pratiques")}</span>
                <Sparkles className="w-3.5 h-3.5" />
              </NavLink>
            </nav>

            {/* Drawer footer — logout for auth · signup+login for anon */}
            <div className="px-3 py-4 border-t border-border">
              {user ? (
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    logout();
                    navigate("/login");
                  }}
                  data-testid="nav-mobile-logout"
                  className="w-full font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-3 border border-border text-muted-foreground hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors flex items-center justify-center gap-2"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {t("nav.logout")}
                </button>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/signup"
                    onClick={() => setMobileOpen(false)}
                    data-testid="nav-mobile-signup"
                    className="w-full font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-3 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {t("nav.signup_cta", "Créer un compte")}
                  </Link>
                  <Link
                    to="/login"
                    onClick={() => setMobileOpen(false)}
                    data-testid="nav-mobile-signin"
                    className="w-full font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-3 border border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors flex items-center justify-center gap-2"
                  >
                    {t("nav.signin")}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};
